import { TDownloadFileQueue, EDownloadFileStatus } from '../ducks/download-panel/types-download';
import type { Dispatch } from 'redux';
import { useRef, useCallback } from 'react';
import { updateDownloadFileQueueStatus } from '../ducks/download-panel/download-panel';

/**
 * React hook to handle cadence and polling for the download queue using useRef for polling intervals.
 * Returns a handler function to be used for each file in the queue.
 */
export function useHandleDownloadQueueCadence() {
  const pollingIntervals = useRef<Record<string, NodeJS.Timeout>>({});

  // Cleanup function to clear all intervals (call on unmount if needed)
  const cleanupDownloadQueuePolling = useCallback(() => {
    Object.values(pollingIntervals.current).forEach(clearInterval);
    pollingIntervals.current = {};
  }, []);

  // Handler function for queue cadence logic
  const handleDownloadQueueCadence = useCallback((file: TDownloadFileQueue, dispatch: Dispatch, downloadStatusMap: Map<string, string>) => {
    const prevStatus = downloadStatusMap.get(file.id);

    // If the file is in BEGIN and was not before, execute the queue function
    if (file.status === EDownloadFileStatus.BEGIN && prevStatus !== EDownloadFileStatus.BEGIN) {
      downloadStatusMap.set(file.id, EDownloadFileStatus.BEGIN);
      void (async () => {
        try {
          const response = await file.apiQueueFunction();
          if (response === EDownloadFileStatus.IN_PROGRESS) {
            downloadStatusMap.set(file.id, EDownloadFileStatus.QUEUE_COMPLETED);
            dispatch(updateDownloadFileQueueStatus({ id: file.id, status: EDownloadFileStatus.QUEUE_COMPLETED }));
          } else if (response === EDownloadFileStatus.ERROR || response === EDownloadFileStatus.NO_DATA_FOUND) {
            downloadStatusMap.set(file.id, EDownloadFileStatus.ERROR);
            dispatch(updateDownloadFileQueueStatus({ id: file.id, status: EDownloadFileStatus.ERROR }));
          } else if (response === EDownloadFileStatus.SUCCEEDED) {
            downloadStatusMap.set(file.id, EDownloadFileStatus.COMPLETED);
            dispatch(updateDownloadFileQueueStatus({ id: file.id, status: EDownloadFileStatus.COMPLETED }));
          }
        } catch {
          downloadStatusMap.set(file.id, EDownloadFileStatus.ERROR);
          dispatch(updateDownloadFileQueueStatus({ id: file.id, status: EDownloadFileStatus.ERROR }));
        }
      })();
    }

    // If the file is in QUEUE_COMPLETED and no polling is active, start polling
    if (file.status === EDownloadFileStatus.QUEUE_COMPLETED && !pollingIntervals.current[file.id]) {
      pollingIntervals.current[file.id] = setInterval(() => {
        void (async () => {
          try {
            const status = await file.apiCheckStatusFunction();
            if (status === EDownloadFileStatus.SUCCEEDED) {
              clearInterval(pollingIntervals.current[file.id]);
              delete pollingIntervals.current[file.id];
              downloadStatusMap.set(file.id, EDownloadFileStatus.COMPLETED);
              dispatch(updateDownloadFileQueueStatus({ id: file.id, status: EDownloadFileStatus.COMPLETED }));
            } else if (
              status === EDownloadFileStatus.ERROR ||
              status === EDownloadFileStatus.NO_DATA_FOUND ||
              status === EDownloadFileStatus.PARTIALLY_COMPLETED
            ) {
              clearInterval(pollingIntervals.current[file.id]);
              delete pollingIntervals.current[file.id];
              downloadStatusMap.set(file.id, status);
              dispatch(updateDownloadFileQueueStatus({ id: file.id, status: status }));
            }
          } catch {
            clearInterval(pollingIntervals.current[file.id]);
            delete pollingIntervals.current[file.id];
            downloadStatusMap.set(file.id, EDownloadFileStatus.ERROR);
            dispatch(updateDownloadFileQueueStatus({ id: file.id, status: EDownloadFileStatus.ERROR }));
          }
        })();
      }, 2000);
    }
  }, []);

  return { handleDownloadQueueCadence, cleanupDownloadQueuePolling };
}
